// Leitura no banco da comissão do pós-venda por indicação (#6236). A regra fica em
// comissaoIndicacaoPosVenda.ts (pura, testada); aqui só se descobre, para cada negociação
// aprovada, quem registrou a indicação que a gerou.
import { supabase } from './supabase';
import { posVendaPorNegociacao } from './comissaoIndicacaoPosVenda';

/** O usuário atual não consegue ler as indicações: uma resposta vazia não diria se "não há
 *  indicação" ou "não posso ver" — quem chama não pode tratar isso como "nenhuma". */
export class IndicacoesIndisponiveisError extends Error {
    constructor() {
        super('Não foi possível ler as indicações do pós-venda com este usuário. Aplique a migration 20261020130000_indicacao_registros_das_negociacoes.sql ou peça a um gerente para recalcular.');
        this.name = 'IndicacoesIndisponiveisError';
    }
}

const LOTE = 200;

/**
 * Para cada negociação, quem do pós-venda a gerou por indicação (Map negociacao_id → profile id),
 * e o nome dessas pessoas.
 *
 * Lê pela função do banco `fn_indicacao_registros_das_negociacoes` (qualquer usuário interno).
 * Se ela ainda não existir, lê a tabela `indicacoes` direto — mas só confia nessa leitura se o
 * usuário puder gerir o programa (a RLS da tabela); senão lança IndicacoesIndisponiveisError.
 */
export async function buscarPosVendaDasNegociacoes(
    negs: Array<{ id: number | string; responsavel_id?: string | null }>,
): Promise<{ porNegociacao: Map<number, string>; nomes: Map<string, string> }> {
    const vazio = { porNegociacao: new Map<number, string>(), nomes: new Map<string, string>() };
    const ids = [...new Set(negs.map(n => Number(n.id)).filter(n => Number.isFinite(n)))];
    if (ids.length === 0) return vazio;

    let registros: Array<{ negociacao_id: number; registrado_por: string | null }> = [];
    const rpc = await supabase.rpc('fn_indicacao_registros_das_negociacoes', { p_negociacao_ids: ids });
    if (!rpc.error) {
        registros = (rpc.data || []) as typeof registros;
    } else {
        const pode = await supabase.rpc('fn_indicacao_pode_gerir');
        if (pode.error || pode.data !== true) throw new IndicacoesIndisponiveisError();
        for (let i = 0; i < ids.length; i += LOTE) {
            const { data, error } = await supabase
                .from('indicacoes')
                .select('negociacao_id, registrado_por')
                .eq('vale', true)
                .in('negociacao_id', ids.slice(i, i + LOTE));
            if (error) throw error;
            registros.push(...((data || []) as typeof registros));
        }
    }
    if (registros.length === 0) return vazio;

    const quem = [...new Set(registros.map(r => r.registrado_por).filter(Boolean))] as string[];
    const { data: perfis, error } = await supabase
        .from('profiles')
        .select('id, full_name, role, departamento, departamentos, permissions')
        .in('id', quem);
    if (error) throw error;

    const lista = (perfis || []) as any[];
    const perfilPorId = new Map<string, any>(lista.map(p => [String(p.id), p]));
    const responsavelPorNegociacao = new Map<number, string | null>(
        negs.map(n => [Number(n.id), n.responsavel_id ? String(n.responsavel_id) : null]),
    );
    return {
        porNegociacao: posVendaPorNegociacao(registros, perfilPorId, responsavelPorNegociacao),
        nomes: new Map(lista.map(p => [String(p.id), p.full_name || 'Pós-venda'])),
    };
}
